import { pick } from 'lodash';
import { combineReducers } from 'redux';
import { call, debounce, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { getInstallmentListAction } from './installments/ducks';
import { selectPaymentsOverviewPagination, setPaymentOverViewPageAction } from './paymentsOverview/ducks';
import { environment } from 'environments/environment';
import { CustomerAccountBasic } from 'generated/bffCustomerAccount/models';
import {
  FinancialDocumentBasic,
  FinancialDocumentListResponse,
  OpenBalancesResult,
} from 'generated/bffFinancialInfo/models';
import { CampaignOffer } from 'generated/bffOffer/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import dayjs from 'helpers/dayjs';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { getCurrentRoute } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import {
  BillingHeaderFilterPayload,
  BillingListFilterParams,
  BillingReqeustFilter,
  CampaignListPayload,
} from 'models/Billing';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { Pagination } from 'models/Pagination';
import { pushAdobeAnalyticsEventAction, pushAdobeAnalyticsEventFormSubmitAction } from 'saga/adobeAnalytics/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import {
  ContextCustomerAccountActionTypes,
  selectContextCustomerAccountInfo,
} from 'saga/context/customerAccount/ducks';
import { CustomerAccountActionTypes } from 'saga/customerAccount/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

/* STATE */
export interface BillingState {
  financialDocumentBasicList: FinancialDocumentBasic[];
  loadingStatus: LoadingStatus;
  totalCount: number;
  filter: BillingListFilterParams;
  pagination: Pagination;
  selectedCustomerAccount: CustomerAccountBasic | null | undefined;
  selectedSubordinate: boolean;
  selectedFilterSubordinate: boolean;
  balance: OpenBalancesResult | null;
  campaignList: CampaignOffer[];
  richmediaBanners: CampaignOffer[];
  initialRunInfo: boolean;
}

/* ACTION TYPES */
export enum BillingActionTypes {
  SetFilter = '@@Billing/SET_FILTER',
  SetFilterOnly = '@@Billing/SET_FILTER_ONLY',
  SetLoading = '@@Billing/SET_LOADING',
  SetFilterDebounced = '@@Billing/SET_FILTER_DEBOUNCED',
  SetPage = '@@Billing/SET_PAGE',
  GetBillingList = '@@Billing/GET_BILLING_LIST',
  SetCustomerAccount = '@@Billing/SET_CUSTOMER_ACCOUNT',
  SetSubordinate = '@@Billing/SET_SUBORDINATE',
  SetFilterSubordinate = '@@Billing/SET_FILTER_SUBORDINATE',
  GetOpenBalanceList = '@@Billing/GET_OPEN_BALANCE_LIST',
  GetCampaignList = '@@Billing/GET_CAMPAIGN_LIST',
  GetRichmediaBanners = '@@Billing/GET_RICHMEDIA_BANNER',
  SetInitialRunInfo = '@@Billing/SET_INITIAL_RUN_INFO',
  SetPagination = '@@Billing/SET_PAGINATION',
}

/* ACTIONS */
export const setFilterAction = createActionCreator(BillingActionTypes.SetFilter);
export const setFilterOnlyAction = createActionCreator(BillingActionTypes.SetFilterOnly);
export const setLoadingAction = createActionCreator(BillingActionTypes.SetLoading);
export const setFilterDebouncedAction = createActionCreator(BillingActionTypes.SetFilterDebounced);
export const setPageAction = createActionCreator(BillingActionTypes.SetPage);
export const getBillingListActions = createApiActionCreators(BillingActionTypes.GetBillingList);
export const setCustomerAccountAction = createActionCreator(BillingActionTypes.SetCustomerAccount);
export const setSubordinateAction = createActionCreator(BillingActionTypes.SetSubordinate);
export const setFilterSubordinateAction = createActionCreator(BillingActionTypes.SetFilterSubordinate);
export const getOpenBalanceListActions = createApiActionCreators(BillingActionTypes.GetOpenBalanceList);
export const getCampaignListActions = createApiActionCreators(BillingActionTypes.GetCampaignList);
export const getRichmediaBannersActions = createApiActionCreators(BillingActionTypes.GetRichmediaBanners);
export const setInitialRunAction = createActionCreator(BillingActionTypes.SetInitialRunInfo);
export const setPaginationAction = createActionCreator(BillingActionTypes.SetPagination);

/* REDUCERS */
const initialState: BillingState = {
  financialDocumentBasicList: [],
  loadingStatus: LoadingStatus.loading,
  totalCount: 0,
  filter: {},
  pagination: {
    page: 1,
    pageSize: 0,
    forceCheck: false,
    initPages: 1,
  },
  selectedCustomerAccount: null,
  selectedSubordinate: false,
  selectedFilterSubordinate: false,
  balance: null,
  campaignList: [],
  richmediaBanners: [],
  initialRunInfo: true,
};

const financialDocumentBasicList = createReducer(initialState.financialDocumentBasicList, {
  [BillingActionTypes.GetBillingList]: {
    [RequestActionTypes.SUCCESS]: (state: FinancialDocumentBasic[], payload: any & Pagination) => {
      if (payload.page > 1) {
        return [...state, ...(payload.financialDocuments || [])];
      }

      return payload.financialDocuments || [];
    },
    [RequestActionTypes.FAILURE]: () => [],
    [RequestActionTypes.REQUEST]: () => [],
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [BillingActionTypes.GetBillingList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
      RequestActionTypes.TIMED_OUT,
    ],
  },
  {
    [BillingActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const totalCount = createReducer(initialState.totalCount, {
  [BillingActionTypes.GetBillingList]: {
    [RequestActionTypes.SUCCESS]: (_state: number, payload: FinancialDocumentListResponse & Pagination) => {
      return payload.totalItems || 0;
    },
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const filter = createReducer(initialState.filter, {
  [BillingActionTypes.SetFilter]: (state: BillingListFilterParams, payload: Partial<BillingListFilterParams>) => ({
    ...state,
    ...payload,
  }),
  [BillingActionTypes.SetFilterOnly]: (state: BillingListFilterParams, payload: Partial<BillingListFilterParams>) => ({
    ...state,
    ...payload,
  }),
  [BillingActionTypes.SetFilterDebounced]: (
    state: BillingListFilterParams,
    payload: Partial<BillingListFilterParams>
  ) => ({
    ...state,
    ...payload,
  }),
});

const pagination = createReducer(initialState.pagination, {
  [BillingActionTypes.SetPage]: (_state: Pagination, payload: Pagination) => payload,
  [BillingActionTypes.SetPagination]: (_state: Pagination, payload: Pagination) => payload,
  [BillingActionTypes.SetFilter]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [BillingActionTypes.SetFilterOnly]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [BillingActionTypes.SetFilterDebounced]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [BillingActionTypes.SetCustomerAccount]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [BillingActionTypes.SetSubordinate]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [BillingActionTypes.GetBillingList]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
});

const selectedCustomerAccount = createReducer(initialState.selectedCustomerAccount, {
  [BillingActionTypes.SetCustomerAccount]: (
    _state: CustomerAccountBasic | null | undefined,
    payload: BillingHeaderFilterPayload
  ) => payload.customerAccount,
  [ContextCustomerAccountActionTypes.SetContextCustomerAccount]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountBasic | null, payload: CustomerAccountBasic) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ContextCustomerAccountActionTypes.SetContextCustomerAccountSkipped]: (
    _state: CustomerAccountBasic | null,
    payload: CustomerAccountBasic
  ) => payload,
  [CustomerAccountActionTypes.GetCustomerAccountList]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountBasic, payload: CustomerAccountBasic[]) =>
      payload?.length === 1 ? payload[0] : _state,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const selectedSubordinate = createReducer(initialState.selectedSubordinate, {
  [BillingActionTypes.SetSubordinate]: (state: boolean, payload: BillingHeaderFilterPayload) => payload.subordinate,
});

const selectedFilterSubordinate = createReducer(initialState.selectedFilterSubordinate, {
  [BillingActionTypes.SetFilterSubordinate]: (state: boolean, payload: boolean) => payload,
});

const balance = createReducer(initialState.balance, {
  [BillingActionTypes.GetOpenBalanceList]: {
    [RequestActionTypes.SUCCESS]: (_state: OpenBalancesResult | null, payload: OpenBalancesResult | null) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const campaignList = createReducer(initialState.campaignList, {
  [BillingActionTypes.GetCampaignList]: {
    [RequestActionTypes.SUCCESS]: (_state: CampaignOffer[], payload: CampaignOffer[]) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const richmediaBanners = createReducer(initialState.richmediaBanners, {
  [BillingActionTypes.GetRichmediaBanners]: {
    [RequestActionTypes.SUCCESS]: (_state: CampaignOffer[], payload: CampaignOffer[]) => payload,
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const initialRunInfo = createReducer(initialState.initialRunInfo, {
  [BillingActionTypes.SetInitialRunInfo]: (state: boolean, payload: boolean) => payload,
});

export default combineReducers<BillingState>({
  financialDocumentBasicList,
  loadingStatus,
  totalCount,
  filter,
  pagination,
  selectedCustomerAccount,
  selectedSubordinate,
  selectedFilterSubordinate,
  balance,
  campaignList,
  richmediaBanners,
  initialRunInfo,
});

/* SELECTORS*/
const selectBilling = (state: AppState) => state.billing;

export const selectBillingLoading = (state: AppState): LoadingStatus => selectBilling(state).loadingStatus;

export const selectBillingReqeustFilter = (state: AppState): BillingListFilterParams => selectBilling(state).filter;

export const selectBillingPagination = (state: AppState): Pagination => selectBilling(state).pagination;

export const selectBillingData = (state: AppState): FinancialDocumentBasic[] =>
  selectBilling(state).financialDocumentBasicList;

export const selectBillingTotalCount = (state: AppState): number => selectBilling(state).totalCount;

export const selectBillingSelectedCA = (state: AppState): CustomerAccountBasic | null | undefined => {
  const selectedCustomerAccount = selectBilling(state).selectedCustomerAccount;

  return selectedCustomerAccount?.paymentResponsible ? selectBilling(state).selectedCustomerAccount : null;
};

export const selectBillingCaRefNo = (state: AppState): string | undefined => {
  const billingCustomerAccount = selectBillingSelectedCA(state);
  // get billing context CA: if not selected in the billing (dropdown in the header), use Customer Account from the context
  const contextCustomerAccount = selectContextCustomerAccountInfo(state);

  return billingCustomerAccount?.paymentResponsible
    ? billingCustomerAccount?.caRefNo
    : contextCustomerAccount?.paymentResponsible
    ? contextCustomerAccount?.caRefNo
    : undefined;
};

export const selectBillingSelectedSubordinate = (state: AppState): boolean => selectBilling(state).selectedSubordinate;

export const selectBillingSelectedFilterSubordinate = (state: AppState): boolean =>
  selectBilling(state).selectedFilterSubordinate;

export const selectBillingBalance = (state: AppState): OpenBalancesResult | null => selectBilling(state).balance;

export const selectBillingErrors = (state: AppState): CampaignOffer[] => selectBilling(state).campaignList;

export const selectInitialRunInfo = (state: AppState): boolean => selectBilling(state).initialRunInfo;

/* SAGAS */
function* getBillingDefaultPagination(initLoad: boolean) {
  const pageSize = Number(selectProperty('gui.financialDocumentList.pageSize'));
  const initPages = Number(selectProperty('gui.financialDocumentList.loadPagesCount.Init'));

  const pagination: Pagination = yield select(selectBillingPagination);

  if (initLoad) {
    yield put(
      setPaginationAction({
        ...pagination,
        initPages,
        pageSize: pageSize || 50,
      })
    );
  } else {
    yield put(
      setPageAction({
        ...pagination,
        initPages,
        pageSize: pageSize || 50,
      })
    );
  }
}

function* getBillingList({
  payload,
}: AppAction<Partial<Pagination> & Partial<BillingListFilterParams> & { filterByPayload?: boolean }>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const currentPageId = getCurrentRoute()?.commonPageData.pageId || '';
  const requestFilter: BillingListFilterParams = yield select(selectBillingReqeustFilter);
  const billingStoredFilter = JSON.parse(String(localStorage.getItem('billingFilter')));
  const isInitialRun = Object.keys(requestFilter).length === 0;

  if (isInitialRun && !payload?.filterByPayload) {
    yield put(
      setFilterAction({
        documentId: currentPageId !== '2.2.1.1' ? payload.documentId || billingStoredFilter?.documentId : undefined,
        documentType: payload.documentType || billingStoredFilter?.documentType,
        caRefNo: payload.caRefNo || billingStoredFilter?.caRefNo,
        issueDateFrom: payload.issueDateFrom || billingStoredFilter?.issueDateFrom,
        issueDateTo: payload.issueDateTo || billingStoredFilter?.issueDateTo,
        unpaidDocumentsFlag: payload.unpaidDocumentsFlag || billingStoredFilter?.unpaidDocumentsFlag,
      })
    );
  }

  yield put(setInitialRunAction(!Object.values(requestFilter).some((item) => item)));

  const pagination: Pagination = yield select(selectBillingPagination);
  const subordinate: boolean = ['2.2.1.1', '2.2.1.4', '2.2.1.6'].includes(currentPageId)
    ? false
    : yield select(selectBillingSelectedSubordinate);

  if (!pagination.pageSize) {
    yield call(getBillingDefaultPagination, true);
  }

  const updatedPagination: Pagination = yield select(selectBillingPagination);

  const payloadPagination: Partial<Pagination> = pick(payload, ['page', 'pageSize', 'forceCheck']);
  const basePagination = {
    ...updatedPagination,
    ...payloadPagination,
  };
  const paginationParam = {
    ...basePagination,
    page: basePagination.page < basePagination.initPages ? 1 : basePagination.page,
    pageSize:
      basePagination.page < basePagination.initPages
        ? basePagination.pageSize * basePagination.initPages
        : basePagination.pageSize,
  };

  const customer: CustomerInfo = yield select(selectContextCustomerInfo);
  const caRefNo: string | undefined = yield select(selectBillingCaRefNo);
  const finalCaRefNo: string | undefined = payload?.caRefNo ?? caRefNo;
  const filter: Array<BillingReqeustFilter> = [
    {
      column: 'cuRefNo',
      compareType: 'EQUAL',
      value: customer?.cuRefNo,
    },
  ];

  if (finalCaRefNo) {
    filter.push({
      column: 'caRefNo',
      compareType: 'EQUAL',
      value: finalCaRefNo,
    });
  } else if (requestFilter.caRefNo) {
    filter.push({
      column: 'caRefNo',
      compareType: 'EQUAL',
      value: requestFilter.caRefNo,
    });
  }

  if (
    payload.filterByPayload
      ? payload?.issueDateFrom
      : requestFilter?.issueDateFrom || billingStoredFilter?.issueDateFrom
  ) {
    filter.push({
      column: 'issueDate',
      compareType: 'BIGGEREQ',
      value: dayjs(
        payload.filterByPayload
          ? payload?.issueDateFrom
          : requestFilter?.issueDateFrom || billingStoredFilter?.issueDateFrom
      )
        .local()
        .format('YYYY-MM-DD'),
    });
  }

  if (payload.filterByPayload ? payload?.issueDateTo : requestFilter?.issueDateTo || billingStoredFilter?.issueDateTo) {
    filter.push({
      column: 'issueDate',
      compareType: 'SMALLEREQ',
      value: dayjs(
        payload.filterByPayload ? payload?.issueDateTo : requestFilter?.issueDateTo || billingStoredFilter?.issueDateTo
      )
        .local()
        .format('YYYY-MM-DD'),
    });
  }

  if (
    payload.filterByPayload ? payload?.documentType : requestFilter?.documentType || billingStoredFilter?.documentType
  ) {
    filter.push({
      column: 'documentType',
      compareType: 'EQUAL',
      value: payload.filterByPayload
        ? payload?.documentType
        : requestFilter?.documentType || billingStoredFilter?.documentType,
    });
  }

  if (
    (payload.filterByPayload ? payload?.documentId : requestFilter?.documentId || billingStoredFilter?.documentId) &&
    currentPageId !== '2.2.1.1'
  ) {
    filter.push({
      column: 'documentId',
      compareType: 'EQUAL',
      value: payload.filterByPayload
        ? payload?.documentId
        : requestFilter?.documentId || billingStoredFilter?.documentId,
    });
  }

  if (
    payload.filterByPayload
      ? payload?.unpaidDocumentsFlag
      : requestFilter?.unpaidDocumentsFlag || billingStoredFilter?.unpaidDocumentsFlag
  ) {
    filter.push({
      column: 'amountToPay',
      compareType: 'BIGGER',
      value: '0',
    });
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.getBillingList,
      yield getApiUrl(environment.urlConfig.financialInfo),
      JSON.stringify(filter),
      paginationParam,
      finalCaRefNo || filter.some((item) => item.column === 'caRefNo') ? subordinate || false : undefined
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getBillingListActions.success({
        ...resp.data,
        ...pagination,
      })
    );
  } else if (resp.status === 408) {
    //note: error handling for situation when server response for some reason times out
    yield put(setLoadingAction(LoadingStatus.timedOut));
  } else {
    yield put(getBillingListActions.failure());
  }
}

function* getOpenBalanceList() {
  const customer: CustomerInfo | null = yield select(selectContextCustomerInfo);
  const caRefNo: string | undefined = yield select(selectBillingCaRefNo);
  const currentPageId = getCurrentRoute()?.commonPageData.pageId || '';
  const subordinate: boolean = ['2.2.1.1', '2.2.1.4', '2.2.1.6'].includes(currentPageId)
    ? false
    : yield select(selectBillingSelectedSubordinate);

  const request: ApiClientRequest = {
    apiCall: call(
      api.getOpenBalanceList,
      yield getApiUrl(environment.urlConfig.financialInfo),
      customer?.cuRefNo,
      caRefNo,
      caRefNo ? subordinate : undefined
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getOpenBalanceListActions.success(resp.data));
  } else {
    yield put(getOpenBalanceListActions.failure());
  }
}

function* getCampaignList({ payload }: AppAction<CampaignListPayload>) {
  const customer: CustomerInfo | null = yield select(selectContextCustomerInfo);
  const slotCode = payload?.slotCode ?? 'BILLING_MSG';
  const request: ApiClientRequest = {
    apiCall: call(
      api.getCampaignList,
      yield getApiUrl(environment.urlConfig.offer),
      slotCode,
      customer?.cuRefNo,
      payload?.campaignSourceSystem,
      payload?.maxCountOfOffers
    ),
    skipUserActivityLog: true,
    suppressError: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (slotCode === 'BILLING_MSG') {
      yield put(getCampaignListActions.success(resp.data));
    } else {
      const campaignOffers: CampaignOffer[] = resp.data;

      yield put(getRichmediaBannersActions.success(campaignOffers));

      const adobeOffersShowData = campaignOffers?.map((offer) => {
        return {
          unicaTreatmentCode: offer.treatmentCode,
          unicaAudienceIdType: offer.audienceIdType,
          unicaAudienceId: offer.audienceId,
          unicaSlotCode: offer.slot,
          onlineOfferCode: offer.offerId,
          offerSourceType: offer.campaignSourceSystem,
        };
      });

      yield put(
        pushAdobeAnalyticsEventAction({
          eventType: 'offersShow',
          eventData: adobeOffersShowData,
        })
      );
    }
  } else {
    if (resp.status === 404) {
      yield put(getKahlActions.request({ infoMessageIds: 'ID0901' }));
    }

    if (slotCode === 'BILLING_MSG') {
      yield put(getCampaignListActions.failure());
    } else {
      yield put(getRichmediaBannersActions.failure());
    }
  }
}

function* handleBillingHeaderFilterChange({ payload }: AppAction<BillingHeaderFilterPayload>) {
  yield put(pushAdobeAnalyticsEventFormSubmitAction('2-2-1-customerAccountChange'));

  const billingCaRefNo: string = yield select(selectBillingCaRefNo);

  if (!payload.customerAccount && billingCaRefNo && !payload.skipCondition) {
    return;
  }

  const currentPageId = getCurrentRoute()?.commonPageData.pageId || '';

  if (['2.2.1', '2.2.3'].includes(currentPageId)) {
    yield put(getInstallmentListAction.request());
    yield put(getBillingListActions.request({ page: 1 }));
    yield put(getOpenBalanceListActions.request());
  }

  if (currentPageId === '2.2.1b') {
    yield put(getInstallmentListAction.request());

    const paymentsPagination: Pagination = yield select(selectPaymentsOverviewPagination);

    yield put(
      // also triggers the getPaymentListActions.request() action
      setPaymentOverViewPageAction({
        ...paymentsPagination,
        page: 1,
      })
    );
  }
}

function* pushAdobeAnalyticsEventFormSubmit() {
  yield put(pushAdobeAnalyticsEventFormSubmitAction('2-2-1-filter'));
}

/* EXPORT */
export function* billingSaga() {
  yield takeLatest(
    [
      createActionType(BillingActionTypes.GetBillingList, RequestActionTypes.REQUEST),
      BillingActionTypes.SetFilter,
      BillingActionTypes.SetPage,
    ],
    getBillingList
  );

  takeLatest([BillingActionTypes.SetFilter, BillingActionTypes.SetFilterOnly], pushAdobeAnalyticsEventFormSubmit);

  yield takeLatest(
    createActionType(BillingActionTypes.GetOpenBalanceList, RequestActionTypes.REQUEST),
    getOpenBalanceList
  );

  yield takeLatest(
    [
      createActionType(BillingActionTypes.GetCampaignList, RequestActionTypes.REQUEST),
      createActionType(BillingActionTypes.GetRichmediaBanners, RequestActionTypes.REQUEST),
    ],
    getCampaignList
  );

  yield takeLatest(
    [createActionType(BillingActionTypes.SetSubordinate), createActionType(BillingActionTypes.SetCustomerAccount)],
    handleBillingHeaderFilterChange
  );

  yield debounce(1000, BillingActionTypes.SetFilterDebounced, getBillingList);
  yield debounce(1000, BillingActionTypes.SetFilterDebounced, pushAdobeAnalyticsEventFormSubmit);
}
